import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import { AlertErrorCircle, ChevronDown, ChevronUp } from '@/assets/icons'
import { selectConversationSelectedFiles } from '@/chat-common/selected_files/store/selectors'
import { toggleSelectedFilesContentExpanded } from '@/chat-common/store/chat-ux.slice'
import { selectFileProcessingStatusesWithKeys } from '@/store/selectors/file-upload-tasks-reducers.selector'
import { FileProcessingStatus } from '@/store/slices/file-upload-tasks.slice'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import { selectConversationSelectedFilesContentExpanded } from '@/chat-common/store/chat-ux.selectors'
import { selectAgentConversationLoading } from '@/agent/conversations/store/selectors'

type SelectedFilesProps = {
  chatId: string
  isOverLimit: boolean
  hasProcessingUserFiles?: boolean
  hasErroredUserFiles?: boolean
  pendingFilesCount?: number
  showLegacyFilesResetNotice?: boolean
}

function SelectedFilesControl(props: SelectedFilesProps) {
  const { chatId, isOverLimit, hasProcessingUserFiles = false, hasErroredUserFiles = false, pendingFilesCount = 0, showLegacyFilesResetNotice = false } = props
  const dispatch = useAppDispatch()

  // Conversation State
  const isLoading = useAppSelector((state: RootState) => selectAgentConversationLoading(state, { conversationId: chatId }))

  // Selected Files State
  const selectedFilePaths = useAppSelector((state: RootState) => selectConversationSelectedFiles(state, { conversationId: chatId }))
  const selectedFileProcessingStatuses = useAppSelector((state: RootState) => selectFileProcessingStatusesWithKeys(state, { keys: selectedFilePaths }))
  const someLegacyErrors = Object.values(selectedFileProcessingStatuses).some((status) => status === FileProcessingStatus.ERROR)
  const someLegacyProcessingOrPending = Object.values(selectedFileProcessingStatuses).some((status) =>
    [FileProcessingStatus.PROCESSING, FileProcessingStatus.PENDING].includes(status as FileProcessingStatus)
  )
  const someErrors = someLegacyErrors || hasErroredUserFiles
  const someProcessingOrPending = someLegacyProcessingOrPending || hasProcessingUserFiles
  const selectedFilesCount = selectedFilePaths.length + pendingFilesCount
  const hasSelectedFiles = selectedFilesCount > 0

  // Inferred version of isExpanded
  const globalIsExpanded = useAppSelector((state: RootState) => selectConversationSelectedFilesContentExpanded(state, { chatId }))
  const isExpanded = globalIsExpanded || someErrors

  const toggleSelectedFilesBox = () => {
    dispatch(toggleSelectedFilesContentExpanded({ conversationId: chatId }))
  }

  if (!hasSelectedFiles && !showLegacyFilesResetNotice) return null

  return (
    <div>
      {(hasSelectedFiles || showLegacyFilesResetNotice) && !someErrors && (
        // Control for collapsing the selected files box and user feedback
        <button
          className={`flex text-sm text-brand-neutral-500 gap-x-1 ${isLoading ? 'cursor-default opacity-50' : 'cursor-pointer'}`}
          onClick={toggleSelectedFilesBox}
          disabled={isLoading}
        >
          {someProcessingOrPending ? (
            <div className={'flex gap-x-2 items-center'}>
              <CircularProgressContinuousSized size={11} thickness={7} /> <div>Processing files...</div>
            </div>
          ) : (
            <div className={'flex gap-x-[5px] items-end'}>
              {/* Warning icon if files are over limit */}
              {isOverLimit && <AlertErrorCircle className="text-yellow-600 self-center size-[15px] mt-[2px]" />}

              {/* Selected files count display */}
              {selectedFilesCount > 0 ? (
                <div>
                  {selectedFilesCount}
                  {selectedFilesCount > 1 ? ' Files' : ' File'}
                </div>
              ) : (
                <div>No Files</div>
              )}
            </div>
          )}
          {isExpanded ? (
            <ChevronDown className={`self-center h-4 w-4 ${isLoading ? 'text-brand-neutral-500' : 'hover:text-brand-500'}`} />
          ) : (
            <ChevronUp className={`self-center h-4 w-4 ${isLoading ? 'text-brand-neutral-500' : 'hover:text-brand-500'}`} />
          )}
        </button>
      )}
      {someErrors && <span className={`flex xs:justify-end m-1 text-red-700 text-xs`}>{someErrors && 'Error processing file(s). Please try again.'}</span>}
    </div>
  )
}

export default SelectedFilesControl
